/**
 * Utility function to pause execution for a specified amount of time
 * @param ms - Time to sleep in milliseconds
 * @returns Promise that resolves after the specified time
 *
 * @example
 * await sleep(500) // Wait 500ms
 * await sleep(1000) // Wait 1 second
 */
export const sleep = (ms: number): Promise<void> => {
  return new Promise(resolve => setTimeout(resolve, ms));
};

/**
 * Retry an async function with configurable retry logic
 * @param asyncFn - The async function to retry
 * @param options - Retry configuration
 * @returns Promise that resolves with the function result
 *
 * @example
 * const data = await withRetry(
 *   () => fetchData(id),
 *   {
 *     maxRetries: 2,
 *     retryDelay: 1000,
 *     isRetryable: (err) => err?.response?.status === 403
 *   }
 * )
 */
export async function withRetry<T>(
  asyncFn: () => Promise<T>,
  options: {
    maxRetries: number;
    retryDelay: number;
    isRetryable: (error: any) => boolean;
  },
): Promise<T> {
  for (let attempt = 1; attempt <= options.maxRetries + 1; attempt++) {
    try {
      const result = await asyncFn();

      // Log success if this was a retry
      if (attempt > 1) {
        console.log(`✅ Operation succeeded after ${attempt} attempts`);
      }

      return result;
    } catch (err) {
      const isLastAttempt = attempt > options.maxRetries;
      const shouldRetry = options.isRetryable(err);

      if (isLastAttempt || !shouldRetry) {
        // Either we've exhausted retries or error is not retryable
        console.error(`❌ Operation failed after ${attempt} attempts:`, err);
        throw err;
      }

      // Retry
      console.warn(
        `⚠️ Operation failed (attempt ${attempt}/${options.maxRetries + 1}), retrying in ${options.retryDelay}ms...`,
        err,
      );
      await sleep(options.retryDelay);
    }
  }

  // This should never be reached due to throw in loop
  throw new Error('Retry logic failed unexpectedly');
}
